import { cn } from "@/components/libs/cn";
import { type ReactNode, useEffect, useRef, useState } from "react";

type AutoScalerProps = {
  children: ReactNode;
  desiredWidth: number;
  desiredHeight?: number;
  className?: string;
  contentClassName?: string;
  alignment?: "start" | "center";
  placeholder?: ReactNode;
};

export function AutoScaler({
  children,
  desiredWidth,
  desiredHeight,
  className,
  contentClassName,
  alignment = "start",
  placeholder,
}: AutoScalerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const containerWidth = entry.contentRect.width;
        const scaleX = containerWidth / desiredWidth;

        if (desiredHeight) {
          const containerHeight = entry.contentRect.height;
          const scaleY = containerHeight / desiredHeight;
          const newScale = Math.min(scaleX, scaleY);
          setScale(newScale);
        } else {
          setScale(scaleX);
        }
      }
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [desiredWidth, desiredHeight]);

  if (scale === null) {
    return (
      <div
        ref={containerRef}
        className={cn("size-full overflow-hidden", className)}
      >
        {placeholder}
      </div>
    );
  }

  const scaledContent = (
    <div
      className={cn("origin-top-left", contentClassName)}
      style={{
        width: `${desiredWidth}px`,
        height: desiredHeight ? `${desiredHeight}px` : `${100 / scale}%`,
        transform: `scale(${scale})`,
        overflow: desiredHeight ? undefined : "hidden",
      }}
    >
      {children}
    </div>
  );

  if (alignment === "center") {
    const centeredWidth = desiredWidth * scale;
    const centeredHeight = desiredHeight ? desiredHeight * scale : null;

    return (
      <div
        ref={containerRef}
        className={cn("size-full overflow-hidden", className)}
      >
        <div className="flex size-full items-center justify-center">
          <div
            style={{
              width: `${centeredWidth}px`,
              height: centeredHeight ? `${centeredHeight}px` : undefined,
            }}
          >
            {scaledContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn("size-full overflow-hidden", className)}
    >
      {scaledContent}
    </div>
  );
}
